import type {ReactElement} from 'react';
import React from 'react';
import {CircularProgress} from '@Components/circular-progress/circular-progress';
import styles from './item-loading-indicator.module.scss';
import type {ItemType} from '@PosterWhiteboard/items/item/item.types';
import type {ItemLoadingState} from '@Components/poster-editor/poster-editor-reducer';
import {useLoadingIndicatorLayout} from './use-loading-indicator-layout';
import {
  LOADING_INDICATOR_HEIGHT,
  LOADING_INDICATOR_PADDING,
  LOADING_INDICATOR_TEXT_GAP,
  LOADING_INDICATOR_FONT_SIZE,
  LOADING_INDICATOR_BORDER_RADIUS,
  LOADING_INDICATOR_CIRCULAR_PROGRESS_DIAMETER,
  LOADING_INDICATOR_CIRCULAR_PROGRESS_BASE_DIAMETER,
} from './item-loading-indicators.types';

export interface ItemLoadingIndicatorProps {
  item: ItemType;
  loadingState: ItemLoadingState;
  posterScale: number;
  fitToScreenScale: number;
  textMeasurementCanvas: CanvasRenderingContext2D | null;
}

export function ItemLoadingIndicator({
  item,
  loadingState,
  posterScale,
  fitToScreenScale,
  textMeasurementCanvas,
}: ItemLoadingIndicatorProps): ReactElement<any> | null {
  const loadingHeight = (LOADING_INDICATOR_HEIGHT / fitToScreenScale) * posterScale;
  const loadingPadding = (LOADING_INDICATOR_PADDING / fitToScreenScale) * posterScale;
  const textGap = (LOADING_INDICATOR_TEXT_GAP / fitToScreenScale) * posterScale;
  const fontSize = (LOADING_INDICATOR_FONT_SIZE / fitToScreenScale) * posterScale;
  const borderRadius = (LOADING_INDICATOR_BORDER_RADIUS / fitToScreenScale) * posterScale;
  const circularProgressDiameterViewport = (LOADING_INDICATOR_CIRCULAR_PROGRESS_DIAMETER / fitToScreenScale) * posterScale;
  const circularProgressScale = circularProgressDiameterViewport / LOADING_INDICATOR_CIRCULAR_PROGRESS_BASE_DIAMETER;

  const layout = useLoadingIndicatorLayout({
    item,
    loadingState,
    fitToScreenScale,
    posterScale,
    textMeasurementCanvas,
  });

  if (!layout) {
    return null;
  }

  const progressPercentage = loadingState.progress * 100;

  return (
    <div
      key={item.uid}
      className={`${styles.loadingIndicator} _position-absolute flexbox flex-items-center`}
      style={{
        visibility: 'visible',
        top: `${layout.position.top}px`,
        left: `${layout.position.left}px`,
        rotate: `${layout.position.angle}deg`,
        transformOrigin: 'top left',
        height: `${loadingHeight}px`,
        width: `${layout.width}px`,
      }}
    >
      <div
        className={`${styles.background} _position-absolute _full-width _full-height`}
        style={{
          borderRadius: `${borderRadius}px`,
        }}
      />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          padding: `0 ${loadingPadding}px`,
          height: `${loadingHeight}px`,
          gap: `${textGap}px`,
        }}
      >
        <div
          className={styles.circularProgress}
          style={{
            transform: `scale(${circularProgressScale})`,
            transformOrigin: 'center center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress value={progressPercentage} />
        </div>
        {layout.loadingText ? (
          <span
            className={styles.text}
            style={{
              fontSize: `${fontSize}px`,
            }}
          >
            {layout.loadingText}
          </span>
        ) : null}
      </div>
    </div>
  );
}
